import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { format, subYears } from 'date-fns';
import { convertReadingsDictionaryToList } from 'helpers/general';
import { decodeSig } from 'helpers/device';
import { useEffect, useState } from 'react';
import { StoredUser } from 'store/features/authentication';
import { useLazyGetDeviceReadingsQuery } from 'store/services';
import { Device, DeviceReadingsResultEntry } from 'types';
import styles from './DeviceReadings.module.scss';

interface DeviceReadingsProps {
    device: Device;
    user: StoredUser | null;
}

export const DeviceReadings = ({ device, user }: DeviceReadingsProps) => {
    const [readings, setReadings] = useState<DeviceReadingsResultEntry[]>([]);

    const [queryDeviceReadings] = useLazyGetDeviceReadingsQuery();

    useEffect(() => {
        if (device?.deviceId && user?.idToken) {
            const endTime = subYears(new Date(), 2);
            queryDeviceReadings({
                deviceId: device.deviceId,
                start: format(endTime, 'yyyy-MM-dd'),
                end: format(new Date(), 'yyyy-MM-dd'),
            }).then(({ data, isSuccess }) => {
                if (isSuccess) {
                    const output = convertReadingsDictionaryToList(data);
                    setReadings(output.data);
                }
            });
        }
    }, [setReadings, device, user, queryDeviceReadings]);

    const columns: GridColDef[] = [
        {
            field: 'time',
            headerName: 'Time',
            flex: 1,
            valueFormatter: (params) =>
                params.value ? format(params.value, 'MM/dd/yy hh:mm a') : '-',
            sortingOrder: ['asc', 'desc'],
        },
        {
            field: 'insert_time',
            headerName: 'Insert Time',
            flex: 1,
            valueFormatter: (params) =>
                params.value ? format(params.value, 'MM/dd/yy hh:mm a') : '-',
            sortingOrder: ['asc', 'desc'],
        },
        {
            field: 'old_time',
            headerName: 'Old Time',
            flex: 1,
            sortingOrder: ['asc', 'desc'],
            valueFormatter: (params) =>
                params.value ? format(params.value, 'MM/dd/yy hh:mm a') : '-',
        },
        {
            field: 'tank',
            headerName: 'Tank',
            flex: 1,
            sortingOrder: ['asc', 'desc'],
            valueGetter: (params) => params.row?.tank?.toFixed(2),
        },
        {
            field: 'battery',
            headerName: 'Battery',
            flex: 1,
            sortingOrder: ['asc', 'desc'],
        },
        {
            field: 'temperature',
            headerName: 'Temperature (˚F)',
            flex: 1,
            sortingOrder: ['asc', 'desc'],
            valueGetter: (params) => params.row?.temperature?.toFixed(2),
        },
        {
            field: 'voltage',
            headerName: 'Voltage',
            flex: 1,
            sortingOrder: ['asc', 'desc'],
        },
        {
            field: 'sensor_voltage',
            headerName: 'Sensor Voltage',
            flex: 1,
            sortingOrder: ['asc', 'desc'],
        },
        {
            field: 'sw_rev',
            headerName: 'SW_Rev',
            flex: 1,
            sortingOrder: ['asc', 'desc'],
        },
        {
            field: 'mdm_rev',
            headerName: 'MDM_Rev',
            flex: 1,
            sortingOrder: ['asc', 'desc'],
        },
        {
            field: 'sig',
            headerName: ' Decoded Signature',
            flex: 1,
            sortingOrder: ['asc', 'desc'],
            valueGetter: (params) =>
                params.row?.sig ? decodeSig(params.row.sig) : '-',
        },
    ];

    return (
        <div data-testid="DeviceReadings" className={styles.container}>
            <DataGrid
                columns={columns}
                rows={readings}
                getRowId={(reading) => reading._id}
                components={{ Toolbar: GridToolbar }}
                initialState={{
                    sorting: {
                        sortModel: [{ field: 'time', sort: 'asc' }],
                    },
                    columns: {
                        columnVisibilityModel: {
                            old_time: false,
                            sw_ref: false,
                            mdm_rev: false,
                            sig: false,
                        },
                    },
                }}
                disableSelectionOnClick
                disableColumnMenu
            />
        </div>
    );
};
